import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Container, Dropdown, Spinner, Table } from "react-bootstrap";
import { CheckCircle2, Mail, MoreHorizontal, RefreshCw, Send, UserRound } from "lucide-react";
import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import "./billing-alerts.css";
type CardAlert = {
  alert_id?: number | null;
  alert_status?: string;
  alert_priority?: string;
  brand?: string;
  category: string;
  company_name?: string;
  customer_name: string;
  days_remaining: number;
  expiry_date: string;
  last4?: string;
  last_customer_reminder_at?: string | null;
  monthly_amount?: string;
  source: string;
  source_card_id: string;
  subscription_plan?: string;
  user_email?: string;
  user_id?: number;
};

type WidgetResponse = {
  categories: Record<string, CardAlert[]>;
  total: number;
};

type PaymentAlert = {
  amount?: string | number;
  company_name?: string;
  created_at?: string;
  failure_reason?: string;
  gateway: string;
  priority?: string;
  status: string;
  transaction_id: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
};

const CATEGORY_ORDER = [
  "Expiring in 30 Days",
  "Expiring in 14 Days",
  "Expiring in 7 Days",
  "Expiring in 1 Day",
  "Expired",
];

function getBadgeVariant(category: string, priority?: string) {
  if (priority === "high" || category === "Expired") return "danger";
  if (category === "Expiring in 1 Day") return "warning";
  if (category === "Expiring in 7 Days") return "info";
  return "secondary";
}

function formatDate(value?: string | null) {
  if (!value) return "Not sent yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatExpiryDistance(days: number) {
  if (days < 0) return `Expired ${Math.abs(days)} ${Math.abs(days) === 1 ? "day" : "days"} ago`;
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires tomorrow";
  return `Expires in ${days} days`;
}

function titleCase(value?: string) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function CardsExpiringSoon() {
  const [data, setData] = useState<WidgetResponse>({ categories: {}, total: 0 });
  const [paymentAlerts, setPaymentAlerts] = useState<PaymentAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState<string>("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const rows = useMemo(
    () => CATEGORY_ORDER.flatMap((category) => data.categories?.[category] || []),
    [data.categories]
  );

  const loadWidget = async () => {
    setError("");
    setLoading(true);
    try {
      const response: any = await CallAPIGetPromise("/v2/api/billing-alerts/cards-expiring-soon");
      const paymentResponse: any = await CallAPIGetPromise("/v2/api/billing-alerts/payment-alerts?limit=25");
      setData(response?.data || { categories: {}, total: 0 });
      setPaymentAlerts(paymentResponse?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load cards expiring soon.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWidget();
  }, []);

  const runAction = async (key: string, action: () => Promise<void>, message: string) => {
    setError("");
    setSuccess("");
    setWorkingId(key);
    try {
      await action();
      setSuccess(message);
      await loadWidget();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Action failed.");
    } finally {
      setWorkingId("");
    }
  };

  const scanNow = () =>
    runAction(
      "scan",
      async () => {
        await CallAPIPostPromise("/v2/api/billing-alerts/scan-card-expiry", { force: false });
      },
      "Card expiry scan completed."
    );

  const resendReminder = (row: CardAlert) => {
    if (!row.alert_id) {
      return scanNow();
    }
    return runAction(
      `resend-${row.alert_id}`,
      async () => {
        await CallAPIPostPromise(`/v2/api/billing-alerts/cards/${row.alert_id}/resend`, {});
      },
      "Reminder resent to the customer and admin."
    );
  };

  const updateStatus = (row: CardAlert, status: "contacted" | "resolved") => {
    if (!row.alert_id) {
      return scanNow();
    }
    return runAction(
      `${status}-${row.alert_id}`,
      async () => {
        await CallAPIPostPromise(`/v2/api/billing-alerts/cards/${row.alert_id}/status`, { status });
      },
      status === "resolved" ? "Card alert marked resolved." : "Card alert marked contacted."
    );
  };

  return (
    <div className="page-content billing-cards-page">
      <Container fluid>
        <div className="billing-cards-page__toolbar">
            <Button variant="light" onClick={loadWidget} disabled={loading}>
              <RefreshCw size={16} className="me-1" /> Refresh
            </Button>
            <Button variant="primary" onClick={scanNow} disabled={Boolean(workingId)}>
              {workingId === "scan" ? <Spinner animation="border" size="sm" className="me-2" /> : <Send size={16} className="me-1" />}
              Run Scan
            </Button>
        </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <div className="billing-expiry-stats">
        {CATEGORY_ORDER.map((category) => (
            <Card key={category} className={`billing-expiry-stat${category === "Expired" ? " billing-expiry-stat--danger" : ""}`}>
              <Card.Body>
                <div className="text-muted small">{category}</div>
                <div className="fs-3 fw-semibold">{data.categories?.[category]?.length || 0}</div>
              </Card.Body>
            </Card>
        ))}
      </div>

      <Card className="billing-alerts-card">
        <Card.Body>
          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          ) : rows.length === 0 ? (
            <Alert variant="success" className="mb-0">
              <CheckCircle2 size={18} className="me-2" />
              No saved cards are inside the expiry window.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="billing-alerts-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Company</th>
                    <th>Card</th>
                    <th>Expiry</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Last Reminder</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const rowKey = `${row.source}-${row.source_card_id}`;
                    return (
                      <tr key={rowKey}>
                        <td>
                          <Badge bg={getBadgeVariant(row.category, row.alert_priority)}>{row.category}</Badge>
                          {row.alert_status ? <div className="billing-resolution-status">Alert {titleCase(row.alert_status)}</div> : null}
                        </td>
                        <td>
                          <div className="fw-semibold">{row.customer_name}</div>
                          <div className="small text-muted">{row.user_email || "No email"}</div>
                        </td>
                        <td>{row.company_name || "—"}</td>
                        <td>
                          <div className="billing-card-number"><span>{titleCase(row.brand) || "Card"}</span> •••• {row.last4 || "—"}</div>
                        </td>
                        <td>
                          <div>{row.expiry_date}</div>
                          <div className={`billing-expiry-distance${row.days_remaining < 0 ? " is-expired" : ""}`}>{formatExpiryDistance(row.days_remaining)}</div>
                        </td>
                        <td>{titleCase(row.subscription_plan) || "—"}</td>
                        <td>${Number(row.monthly_amount || 0).toFixed(2)}</td>
                        <td>{formatDate(row.last_customer_reminder_at)}</td>
                        <td className="billing-row-actions-cell">
                          <div className="billing-row-actions">
                            <Button size="sm" variant="outline-primary" onClick={() => resendReminder(row)} disabled={Boolean(workingId)}>
                              <Send size={14} className="me-1" /> Resend
                            </Button>
                            <Dropdown align="end">
                              <Dropdown.Toggle variant="light" size="sm" className="billing-more-actions" aria-label={`More actions for ${row.customer_name}`}>
                                <MoreHorizontal size={16} />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item href={`/pages-single-contractors?id=${row.user_id}`}>
                                  <UserRound size={14} /> View customer
                                </Dropdown.Item>
                                <Dropdown.Item
                                  href={row.user_email ? `mailto:${row.user_email}` : undefined}
                                  disabled={!row.user_email}
                                  onClick={() => updateStatus(row, "contacted")}
                                >
                                  <Mail size={14} /> Contact customer
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => updateStatus(row, "resolved")} disabled={Boolean(workingId)} className="text-success">
                                  <CheckCircle2 size={14} /> Mark resolved
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="billing-alerts-card mt-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="mb-1">Recent Payment Alerts</h5>
              <div className="text-muted small">All payment activity and card declines sent to admin email and notification center.</div>
            </div>
          </div>
          {paymentAlerts.length === 0 ? (
            <Alert variant="light" className="mb-0">No payment alerts recorded yet.</Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Customer</th>
                    <th>Company</th>
                    <th>Gateway</th>
                    <th>Amount</th>
                    <th>Transaction</th>
                    <th>Reason</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentAlerts.map((alert) => {
                    const customer = [alert.user_first_name, alert.user_last_name].filter(Boolean).join(" ") || "N/A";
                    return (
                      <tr key={`${alert.gateway}-${alert.transaction_id}-${alert.status}`}>
                        <td>
                          <Badge bg={alert.status === "failed" ? "danger" : "success"}>{alert.status}</Badge>
                          {alert.priority === "high" ? <div className="small text-danger mt-1">High Priority</div> : null}
                        </td>
                        <td>
                          <div className="fw-semibold">{customer}</div>
                          <div className="small text-muted">{alert.user_email || "N/A"}</div>
                        </td>
                        <td>{alert.company_name || "N/A"}</td>
                        <td>{alert.gateway}</td>
                        <td>${Number(alert.amount || 0).toFixed(2)}</td>
                        <td className="text-break">{alert.transaction_id}</td>
                        <td>{alert.failure_reason || "N/A"}</td>
                        <td>{formatDate(alert.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      </Container>
    </div>
  );
}
